import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  CFLocation,
  CFLocationType,
  CreateCFLocationPayload,
  PaginatedResponse,
} from '@/lib/types';

export interface CFLocationFilters {
  page?: number;
  page_size?: number;
  type?: CFLocationType | '';
  is_active?: boolean;
  q?: string;
  ordering?: string;
}

export function useCFLocations(filters: CFLocationFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.cfLocations.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<CFLocation>> => {
      const response = await apiClient.get<PaginatedResponse<CFLocation>>(
        '/api/v1/cf-locations/',
        { params: cleanFilters }
      );
      return response.data;
    },
  });
}

export function useCFLocation(id: number) {
  return useQuery({
    queryKey: queryKeys.cfLocations.detail(id),
    queryFn: async (): Promise<CFLocation> => {
      const response = await apiClient.get<CFLocation>(`/api/v1/cf-locations/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateCFLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCFLocationPayload): Promise<CFLocation> => {
      const response = await apiClient.post<CFLocation>('/api/v1/cf-locations/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cfLocations.all });
    },
  });
}

export function useUpdateCFLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateCFLocationPayload>;
    }): Promise<CFLocation> => {
      const response = await apiClient.patch<CFLocation>(`/api/v1/cf-locations/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cfLocations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.cfLocations.detail(variables.id) });
    },
  });
}

export function useAllCFLocations() {
  return useQuery({
    queryKey: queryKeys.cfLocations.lists(),
    queryFn: async (): Promise<CFLocation[]> => {
      const locations: CFLocation[] = [];
      let nextUrl: string | null = '/api/v1/cf-locations/';
      let isFirstPage = true;

      while (nextUrl) {
        const response: { data: PaginatedResponse<CFLocation> } = await apiClient.get(
          nextUrl,
          isFirstPage ? { params: { page_size: 200 } } : undefined
        );
        locations.push(...response.data.results);
        nextUrl = response.data.next;
        isFirstPage = false;
      }

      return locations;
    },
  });
}
